import React from 'react';
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Divider,
  FormControl,
  Paper,
  TextField,
  Typography,
  InputAdornment
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { DebounceInput } from 'react-debounce-input';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useMutation } from 'react-fetching-library';

import { AlertDialogContainer } from '../others/AlertDialogContainer';
import { setToken, setGroup } from '../../services/redux-actions';
import { registerAction } from '../../services/request';
import { useFullScreen } from '../../services/hooks';
import Footer from '../../layouts/landing/Footer';

export const RegistrationPage = () => {
  const mediaWidth = useFullScreen() ? '300px' : '450px';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutate } = useMutation(registerAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');

  const validationSchema = Yup.object({
    email: Yup.string().email('Proszę podać poprawny adres e-mail')
      .required('To pole nie może być puste'),
    password1: Yup.string()
      .required('To pole nie może być puste')
      .min(8, 'Hasło musi mieć co najmniej 8 znaków')
      .max(128, 'Hasło może mięc najwyżej 128 znaków'),
    password2: Yup.string()
      .required('To pole nie może być puste')
      .min(8, 'Hasło musi mieć co najmniej 8 znaków')
      .max(128, 'Hasło może mięc najwyżej 128 znaków')
      .oneOf([Yup.ref('password1')], 'Wpisz poprawne hasło')
  });

  const initialValues = {
    email: '',
    password1: '',
    password2: ''
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values);
    if (!error) {
      dispatch(setToken(payload.token));
      dispatch(setGroup(payload.user.groups[0]));
      navigate('/client');
    } else {
      setSeverity('error');
      setTitle('Błąd');
      setMessage(payload.message);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <Box display="flex" flexDirection="column" minHeight="100%">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        component={Paper}
        elevation={4}
        padding={8}
      >
        {showAlert && (
          <AlertDialogContainer
            severity={severity}
            title={title}
            message={message}
            setShowAlert={setShowAlert}
          />
        )}
        <Box>
          <Typography color="primary" variant="h5" align="center">
          Rejestracja
          </Typography>
        </Box>
        <Divider style={{ width: mediaWidth, marginTop: 20 }} />
        <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <FormControl
            style={{
              marginTop: 50,
              marginBottom: 20,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography color="primary" variant="h6">
            Adres e-mail
            </Typography>
            <DebounceInput
              variant="outlined"
              name="email"
              id="email"
              type="text"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              debounceTimeout={100}
              element={TextField}
              style={{ marginRight: 10, marginBottom: 10 }}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                )
              }}
            />
          </FormControl>
          <FormControl
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography color="primary" variant="h6">
            Hasło
            </Typography>
            <DebounceInput
              variant="outlined"
              type="password"
              name="password1"
              id="password1"
              value={formik.values.password1}
              onChange={formik.handleChange}
              error={formik.touched.password1 && Boolean(formik.errors.password1)}
              helperText={formik.touched.password1 && formik.errors.password1}
              debounceTimeout={100}
              element={TextField}
              style={{ marginRight: 10, marginBottom: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOpenIcon />
                  </InputAdornment>
                )
              }}
            />
          </FormControl>
          <FormControl
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography color="primary" variant="h6">
            Powtórz hasło
            </Typography>
            <DebounceInput
              variant="outlined"
              type="password"
              name="password2"
              id="password2"
              value={formik.values.password2}
              onChange={formik.handleChange}
              error={formik.touched.password2 && Boolean(formik.errors.password2)}
              helperText={formik.touched.password2 && formik.errors.password2}
              debounceTimeout={100}
              element={TextField}
              style={{ marginRight: 10, marginBottom: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOpenIcon />
                  </InputAdornment>
                )
              }}
            />
          </FormControl>
          <Divider style={{ width: mediaWidth, marginTop: 50, marginBottom: 20 }} />
          <Button
            color="primary"
            variant="contained"
            type="submit"
            style={{ marginBottom: 20 }}
          >
          Zarejestruj
          </Button>
        </form>
        <Box display="flex" flexDirection="row" justifyContent="center">
          <Button onClick={() => navigate('/login')}>
            <Typography variant="subtitle2"><u>Wróć do panelu logowania</u></Typography>
          </Button>
        </Box>
      </Box>
      <Box flex={1}></Box>
      <Footer />
    </Box>
  );
};
